/**
 * Heartbeat активной вкладки → «время в сервисе» (метрики, спека 19 бэка).
 *
 * Раз в минуту, только пока вкладка видима и пользователь что-то делал за
 * последние 5 минут (иначе открытая, но брошенная вкладка считалась бы работой);
 * сразу — при возврате на вкладку. Бэк сам делит время между вкладками и не
 * начисляет простой. Ошибки не показываем: метрики — best-effort.
 *
 * Время учитывается по воркспейсу, поэтому без активного воркспейса (список ещё
 * грузится) не шлём ничего, а при смене воркспейса досылаем отметку старому —
 * иначе прошедшая минута ушла бы новому.
 */
import {useEffect} from 'react';
import {useActiveWorkspace} from '@api/workspaces';
import {IS_MOCK} from '../mock-mode';
import {userMetricsClient} from './client';

const HEARTBEAT_INTERVAL_MS = 60_000;
const IDLE_AFTER_MS = 5 * 60_000;
const ACTIVITY_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'scroll',
  'focus',
] as const;

/** Таймзона браузера — граница «дня» активности. */
export const browserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Moscow';
  } catch {
    return 'Europe/Moscow';
  }
};

export const useActivityHeartbeat = () => {
  const {activeId} = useActiveWorkspace();

  useEffect(() => {
    if (IS_MOCK || !activeId) return;
    let lastInputAt = Date.now();
    const markInput = () => {
      lastInputAt = Date.now();
    };
    const send = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastInputAt > IDLE_AFTER_MS) return;
      void userMetricsClient
        .heartbeat({workspaceSid: activeId, timezone: browserTimezone()})
        .catch(() => undefined);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        markInput();
        send();
      }
    };

    for (const event of ACTIVITY_EVENTS)
      window.addEventListener(event, markInput, {passive: true});
    document.addEventListener('visibilitychange', onVisibility);
    const timer = window.setInterval(send, HEARTBEAT_INTERVAL_MS);
    send();
    return () => {
      for (const event of ACTIVITY_EVENTS)
        window.removeEventListener(event, markInput);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(timer);
      send();
    };
  }, [activeId]);
};
